import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { FileCheck2, Loader2, MessageCircle, X } from 'lucide-react';
import MediaCapture from './media/MediaCapture';
import { ticketStopPlan, TICKET_STOP_STATUSES, MAX_UPDATE_FILES } from './timer/ticketStopModel';
import { getStatusLabel, type TicketStatus } from '../types';
import type { ActiveTimer, OpenWorkRecordInfo } from '../types';
import Dialog from './ui/Dialog';
import PendingDecision from './ui/PendingDecision';
import TaskProgressField, { type TaskProgressValue } from './timer/TaskProgressField';
import { effectiveProgress } from './timer/progressModel';
import { saveTaskProgress } from './timer/saveTaskProgress';
import Field, { FormServerError, rules, useFormValidation, type FormSchema } from './ui/Field';
import { toast } from '../store/toastStore';
import { canCloseAfterTimer, requestCloseAfterTimer } from './timer/timerFlow';
import { formatStops } from './timer/timerModel';
import { formatDurationInput } from './worklogs/durationModel';
import { formatTime } from '../utils/format';
import ClaudeProgressNotes from './claude/ClaudeProgressNotes';
import { toastUnderMinute } from './timer/underMinuteToast';

/** "מה בוצע" needs at least 5 characters (the server rule). */
const FINISH_SCHEMA: FormSchema = { 'tsm-summary': [rules.required(), rules.minLength(5)] };

/**
 * WHICH timer / open work record the modal acts on (B.18 — several timers may
 * run at once, so stop/finish always name their target).
 */
export interface TimerTarget {
  timerId?: string;
  workRecordId?: string | null;
  ticketTitle?: string | null;
  segmentsCount?: number;
  /** false = an open work record without a running timer. */
  running: boolean;
  /** The task / ticket the time is on. */
  ticketId?: string;
  /** 'ticket' | 'task' when the caller knows it (status circles vs. the close option). */
  taskType?: string | null;
  /** The task's status now (the circles start on it). */
  ticketStatus?: string | null;
  /** P-10e (D-82) — the task's saved percent (the bar starts on it). */
  ticketProgress?: number | null;
  /** A sub-task skips the open-sub-tasks check of the close. */
  parentTicketId?: string | null;
}

export function timerTargetFromTimer(timer: ActiveTimer): TimerTarget {
  return {
    timerId: timer.id,
    workRecordId: timer.work_record_id,
    ticketTitle: timer.ticket_title,
    segmentsCount: timer.segments_count,
    running: true,
    ticketId: timer.ticket_id,
    taskType: timer.task_type ?? null,
    ticketStatus: timer.ticket_status ?? null,
    ticketProgress: timer.ticket_progress ?? null,
  };
}

export function timerTargetFromOpenRecord(rec: OpenWorkRecordInfo): TimerTarget {
  return {
    workRecordId: rec.work_record_id,
    ticketTitle: rec.ticket_title,
    segmentsCount: rec.segments_count,
    running: false,
    ticketId: rec.ticket_id,
    taskType: rec.task_type ?? null,
    ticketStatus: rec.ticket_status ?? null,
    ticketProgress: rec.ticket_progress ?? null,
  };
}

/** Server message of a rejected API call (e.g. a locked period), if any. */
export function apiErrorMessage(err: unknown): string | undefined {
  const e = err as { error?: { message?: string }; message?: string } | null;
  return e?.error?.message || undefined;
}

export interface TimerSummaryModalProps {
  /** The timer / open record to finish. */
  target: TimerTarget;
  /** Close the modal without taking any action. */
  onClose: () => void;
  /** Called after the log was finished. */
  onDone: () => void;
}

/**
 * P-10a (D-64) — ✓ "סיום" of a timer's log: the ONE finish window of the timer
 * (the floating timers, Home, an open log). ⏸ never opens a window — it stops
 * at once (`useStopTimer`). This window has:
 *   - "מה בוצע"* (required, at least 5 characters) → POST /api/timer/finish:
 *     closes the running part, then closes MY work record only (D-65);
 *   - the task's status as circles (optional). 🟢 "הושלם" runs the regular
 *     close (`useCloseTicket`: its checks and its 6 s "ביטול", in the
 *     always-mounted `TimerFlowHost`); the server then stops the other
 *     workers' timers on the task and notifies them (D-65). Any other circle
 *     is saved right after the log is finished;
 *   - P-10e (D-82): under the circles, the task's progress bar (steps of
 *     10%, synced with the circles — `TaskProgressField`); saved right after
 *     the log is finished, with the status;
 *   - every stop of this log ("09:00–09:42 · 10:15–11:03").
 * A service ticket keeps its "וגם לסגור את הקריאה" option instead of the
 * circles (D-47: service tickets stay as they are).
 */
export default function TimerSummaryModal({
  target,
  onClose,
  onDone,
}: TimerSummaryModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [summary, setSummary] = useState('');
  const [closeAfter, setCloseAfter] = useState(false);
  const isTask = target.taskType === 'task' && !!target.ticketId && !!target.ticketStatus;
  // D-146 — a service request: its status, and "שליחת עדכון ללקוח" (text, files, voice, screen).
  const isServiceTicket = target.taskType === 'ticket' && !!target.ticketId;
  const [ticketStatus, setTicketStatus] = useState<string>(target.ticketStatus ?? 'in_progress');
  const [updateOpen, setUpdateOpen] = useState(false);
  const [updateText, setUpdateText] = useState('');
  const [updateFiles, setUpdateFiles] = useState<File[]>([]);
  const awaitReply = false; // D-317 §5 (Q2): the status select is the only way to "ממתינה ללקוח".
  // FIX-B7 — a voice / screen recording that was not attached blocks the send.
  const [pendingRecording, setPendingRecording] = useState(false);
  const initial: TaskProgressValue = {
    status: target.ticketStatus ?? 'planning',
    progress: effectiveProgress(target.ticketStatus, target.ticketProgress),
  };
  const [taskValue, setTaskValue] = useState<TaskProgressValue>(initial);
  const status = taskValue.status;

  const ticketTitle = target.ticketTitle;
  const canClose = !!target.ticketId && canCloseAfterTimer();

  const stopsQ = useQuery({
    queryKey: ['timer-record-segments', target.workRecordId],
    queryFn: () => api.getRecordSegments(target.workRecordId as string),
    enabled: !!target.workRecordId,
    staleTime: 0,
  });
  const stops = formatStops(stopsQ.data?.data, (iso) => formatTime(iso));

  const finishMutation = useMutation({
    mutationFn: async () => {
      const res = await api.finishTimer(summary, {
        timerId: target.timerId,
        workRecordId: target.timerId ? undefined : target.workRecordId ?? undefined,
      });
      // D-146 — the update to the customer, then the ticket's status (finishing first: the hours are safe).
      if (isServiceTicket) {
        const plan = ticketStopPlan({
          before: target.ticketStatus ?? null,
          chosen: ticketStatus,
          updateText: updateOpen ? updateText : '',
          fileCount: updateOpen ? updateFiles.length : 0,
          awaitReply: updateOpen && awaitReply,
        });
        try {
          if (plan.sendUpdate) {
            const c = await api.createComment(target.ticketId!, { content: plan.content, ...(plan.awaitReply ? { await_reply: true } : {}) });
            const commentId = c.data?.id;
            if (commentId) {
              for (const f of updateFiles) {
                try { await api.uploadCommentAttachment(target.ticketId!, commentId, f); } catch { toast('error', t('ticketDetail.composer.uploadFailed')); }
              }
            }
          }
          if (plan.setStatus) await api.updateTicketStatus(target.ticketId!, plan.setStatus);
        } catch (err) {
          toast('error', apiErrorMessage(err) || t('ticketDetail.statusChangeFailed'));
        }
      }
      // A changed status other than 🟢, and the bar (D-82): saved right after the log is finished.
      if (isTask) {
        try {
          await saveTaskProgress(target.ticketId!, initial, taskValue);
        } catch (err) {
          toast('error', apiErrorMessage(err) || t('ticketDetail.statusChangeFailed'));
        }
      }
      return res;
    },
    onSuccess: (data) => {
      const minutes = data.data?.total_minutes;
      // FQ-062 (Q-176) — under a minute: a friendly note, not a record.
      if (data.data?.skipped_short) toastUnderMinute(t);
      else toast('success', typeof minutes === 'number'
        ? t('timer.recordClosedWithTime', { time: formatDurationInput(minutes) })
        : t('timer.recordClosed'));
      const close = isTask ? status === 'completed' && target.ticketStatus !== 'completed'
        : isServiceTicket ? ticketStopPlan({ before: target.ticketStatus ?? null, chosen: ticketStatus, updateText: '', fileCount: 0, awaitReply: false }).close
        : closeAfter;
      if (close && canClose && target.ticketId) {
        requestCloseAfterTimer({
          id: target.ticketId,
          title: ticketTitle ?? '',
          task_type: target.taskType ?? null,
          parent_ticket_id: target.parentTicketId ?? null,
        });
      }
      if (target.ticketId) {
        queryClient.invalidateQueries({ queryKey: ['ticket', target.ticketId] });
        queryClient.invalidateQueries({ queryKey: ['all-tasks'] });
        // D-82 — the milestone / project progress follows the task's bar.
        queryClient.invalidateQueries({ queryKey: ['project'] });
        queryClient.invalidateQueries({ queryKey: ['milestone'] });
        // D-86 (QX-18) — "מה בוצע" is now a comment in the task's "שיחה" tab.
        queryClient.invalidateQueries({ queryKey: ['ticket-timeline', target.ticketId] });
      }
      onDone();
    },
  });

  const v = useFormValidation(FINISH_SCHEMA, { 'tsm-summary': summary });
  const busy = finishMutation.isPending;

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('timer.finishDialogTitle')}
      icon={<FileCheck2 size={18} strokeWidth={1.75} className="shrink-0 text-accent-text" aria-hidden="true" />}
      description={ticketTitle ? <bdi className="font-medium text-text">{ticketTitle}</bdi> : undefined}
      size="md"
      dismissible={!busy}
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn btn-secondary" disabled={busy}>{t('common.cancel')}</button>
          <button
            type="submit"
            form="tsm-finish-form"
            disabled={busy}
            aria-busy={busy || undefined}
            className="btn btn-primary"
          >
            {busy && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
            {t('timer.finishAction')}
          </button>
        </>
      )}
    >
      <form id="tsm-finish-form" onSubmit={v.submit(() => {
        if (isServiceTicket && updateOpen && pendingRecording) { toast('error', t('media.pendingRecording')); return; }
        finishMutation.mutate();
      })} noValidate className="space-y-4">
        {/* FIX-CLAUDE-B (D-314 §8): Claude's notes on HER open log, the base for "מה בוצע". */}
        <ClaudeProgressNotes workRecordId={target.workRecordId} />
        <div className="relative">
          {/* D-125 PM-03: an automatic (AI) suggestion for "מה בוצע" */}
          <PendingDecision id="PM-03" className="absolute end-0 top-0" />
          <Field id="tsm-summary" label={t('taskPage.whatWasDone')} required hint={t('form.summaryHint')} error={v.errors['tsm-summary']}>
            <textarea
              className="input min-h-20"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              onBlur={() => v.blur('tsm-summary')}
              placeholder={t('timer.summaryPlaceholder')}
              rows={3}
            />
          </Field>
        </div>

        {isServiceTicket ? (
          <div className="space-y-3">
            <Field id="tsm-ticket-status" label={t('timer.ticketStatus')}>
              <select className="input" value={ticketStatus} onChange={(e) => setTicketStatus(e.target.value)} disabled={busy}>
                {TICKET_STOP_STATUSES.map((s) => (
                  <option key={s} value={s}>{getStatusLabel(s as TicketStatus, t, 'ticket')}</option>
                ))}
              </select>
            </Field>
            {!updateOpen ? (
              <button type="button" className="btn btn-secondary inline-flex items-center gap-1.5" onClick={() => setUpdateOpen(true)} disabled={busy}>
                <MessageCircle size={16} aria-hidden="true" />
                {t('timer.sendUpdate.open')}
              </button>
            ) : (
              <fieldset className="space-y-2 rounded-lg border border-border p-3">
                <legend className="px-1 text-sm font-medium text-text">{t('timer.sendUpdate.title')}</legend>
                <Field id="tsm-update" label={t('timer.sendUpdate.message')} hint={t('timer.sendUpdate.hint')}>
                  <textarea className="input min-h-20" rows={3} value={updateText} onChange={(e) => setUpdateText(e.target.value)} disabled={busy} dir="auto" />
                </Field>
                <MediaCapture
                  remaining={MAX_UPDATE_FILES - updateFiles.length}
                  disabled={busy}
                  onPendingChange={setPendingRecording}
                  onAdd={(files) => setUpdateFiles((prev) => [...prev, ...files])}
                />
                {updateFiles.length > 0 && (
                  <ul className="flex flex-wrap gap-1.5">
                    {updateFiles.map((f, i) => (
                      <li key={`${f.name}-${i}`} className="flex items-center gap-1 rounded-md bg-surface-subtle ps-2 pe-1 py-0.5 text-xs text-text">
                        <bdi className="max-w-40 truncate">{f.name}</bdi>
                        <button type="button" className="btn-icon size-6" aria-label={`${t('common.remove')}: ${f.name}`} onClick={() => setUpdateFiles((prev) => prev.filter((_, j) => j !== i))}>
                          <X size={12} aria-hidden="true" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {/* D-317 §5 (Q2): no "מחכה לתשובה" - "ממתינה ללקוח" is the status select above. */}
              </fieldset>
            )}
          </div>
        ) : isTask ? (
          <div>
            <p className="mb-1 text-sm font-medium text-text">{t('timer.taskStatus')}</p>
            <TaskProgressField
              value={taskValue}
              onChange={setTaskValue}
              disabled={busy}
              title={ticketTitle ?? ''}
            />
          </div>
        ) : canClose && (
          <div className="rounded-lg bg-surface-subtle px-3 py-2.5">
            <label htmlFor="tsm-close-after" className="flex cursor-pointer items-center gap-2 text-sm font-medium text-text">
              <input
                id="tsm-close-after"
                type="checkbox"
                checked={closeAfter}
                onChange={(e) => setCloseAfter(e.target.checked)}
                disabled={busy}
                aria-describedby="tsm-close-after-hint"
                className="h-4 w-4 accent-primary-700"
              />
              {target.taskType === 'ticket' ? t('timer.closeAfterTicket') : t('timer.closeAfterTask')}
            </label>
            <p id="tsm-close-after-hint" className="mt-0.5 ps-6 text-xs text-text-2">{t('timer.closeAfterHint')}</p>
          </div>
        )}

        {!!target.workRecordId && (
          <div>
            <p className="mb-1 text-sm font-medium text-text">{t('timer.stopsTitle')}</p>
            {stopsQ.isLoading ? (
              <p className="text-sm text-text-2">…</p>
            ) : stops.length === 0 ? (
              <p className="text-sm text-text-2">{t('timer.noStops')}</p>
            ) : (
              <p className="text-sm tabular-nums text-text"><bdi dir="ltr">{stops.join(' · ')}</bdi></p>
            )}
          </div>
        )}

        {finishMutation.isError && <FormServerError error={finishMutation.error} fallback={t('timer.stopError')} />}
      </form>
    </Dialog>
  );
}

/** Present a minute count as "Xh Ym" (e.g. 195 → "3h 15m"). */
export function formatMinutes(totalMinutes: number, t: any): string {
  const m = Math.max(0, Math.round(Number(totalMinutes) || 0));
  const h = Math.floor(m / 60);
  const rem = m % 60;
  const hLabel = t?.('common.hoursShort', 'h') ?? 'h';
  const mLabel = t?.('common.minutesShort', 'm') ?? 'm';
  if (h > 0 && rem > 0) return `${h}${hLabel} ${rem}${mLabel}`;
  if (h > 0) return `${h}${hLabel}`;
  return `${rem}${mLabel}`;
}
